import { fromJS, Map } from "immutable";

export const ComponentTypes = {
    IMAGE: "image",
    LABEL: "label",
    ONE_PRODUCT: "one_product",
    SEVEN_PRODUCTS: "seven_products",
    SHAPE_CIRCLE: "circle",
    SHAPE_LINE: "line",
    SHAPE_RECTANGLE: "rect",
    THREE_PRODUCTS: "three_products",
};

export const ComponentProductCount = {
    [ComponentTypes.ONE_PRODUCT]: 1,
    [ComponentTypes.THREE_PRODUCTS]: 3,
    [ComponentTypes.SEVEN_PRODUCTS]: 7,
};

export const SpringSettings = {
    DRAG: { stiffness: 1000, damping: 50 },
    RESIZE: { stiffness: 210, damping: 20 },
};

export const SNAP_DISTANCE = 15;

export const MediaTypes = {
    SINGLE_IMAGE: "SINGLE_IMAGE",
};

export const PanelTypes = {
    PERMISSIONS: "permissions",
};

export const MODES = {
    BOTTOM: "BOTTOM",
    BOTTOM_LEFT: "BOTTOM_LEFT",
    BOTTOM_RIGHT: "BOTTOM_RIGHT",
    LEFT: "LEFT",
    MOVE: "MOVE",
    RIGHT: "RIGHT",
    TOP: "TOP",
    TOP_LEFT: "TOP_LEFT",
    TOP_RIGHT: "TOP_RIGHT",
};

export interface IComponentTypes {
    [name: string]: Map<string, any>;
}

export const TRANSPARENT = "transparent";

const defaults = {
    position: {
        left: 0,
        top: 0,
        z_index: 1000,
    },
    size: {
        height: 140,
        width: 140,
    },
    style: {
        background_color: TRANSPARENT,
        border_width: 0,
        border_color: TRANSPARENT,
    },
};

export const getComponentType = (component) => {
    const keys = Object.keys(ComponentTypes);
    for (const key of keys) {
        const keyToCheck = ComponentTypes[key];
        if (component[keyToCheck] !== undefined) {
            return keyToCheck;
        }
    }
    return null;
};

export const componentDefaults: IComponentTypes = {
    [ComponentTypes.SHAPE_RECTANGLE]: fromJS({
        ...defaults,
        component: ComponentTypes.SHAPE_RECTANGLE,
        [ComponentTypes.SHAPE_RECTANGLE]: {},
        style: {
            ...defaults.style,
            background_color: "#000000",
        },
    }),
    [ComponentTypes.SHAPE_CIRCLE]: fromJS({
        ...defaults,
        component: ComponentTypes.SHAPE_CIRCLE,
        [ComponentTypes.SHAPE_CIRCLE]: {},
        style: {
            ...defaults.style,
            background_color: "#000000",
        },
    }),
    [ComponentTypes.SHAPE_LINE]: fromJS({
        ...defaults,
        component: ComponentTypes.SHAPE_LINE,
        [ComponentTypes.SHAPE_LINE]: {},
        size: { height: 2, width: 140 },
        style: {
            ...defaults.style,
            background_color: "#000000",
        },
    }),
    [ComponentTypes.ONE_PRODUCT]: fromJS({
        ...defaults,
        component: ComponentTypes.ONE_PRODUCT,
        [ComponentTypes.ONE_PRODUCT]: {
            labels: [],
            product_1: {
                product_id: undefined,
                product_image: {
                    content_mode: "ASPECT_FIT",
                    url: undefined,
                },
            },
        },
    }),
    [ComponentTypes.THREE_PRODUCTS]: fromJS({
        ...defaults,
        component: ComponentTypes.THREE_PRODUCTS,
        [ComponentTypes.THREE_PRODUCTS]: {
            labels: [],
            product_1: {
                product_id: undefined,
                product_image: {
                    content_mode: "ASPECT_FIT",
                    url: undefined,
                },
            },
            product_2: {
                product_id: undefined,
                product_image: {
                    content_mode: "ASPECT_FIT",
                    url: undefined,
                },
            },
            product_3: {
                product_id: undefined,
                product_image: {
                    content_mode: "ASPECT_FIT",
                    url: undefined,
                },
            },
        },
    }),
    [ComponentTypes.SEVEN_PRODUCTS]: fromJS({
        ...defaults,
        component: ComponentTypes.SEVEN_PRODUCTS,
        [ComponentTypes.SEVEN_PRODUCTS]: {
            labels: [],
            product_1: {
                product_id: undefined,
                product_image: {
                    content_mode: "ASPECT_FIT",
                    url: undefined,
                },
            },
            product_2: {
                product_id: undefined,
                product_image: {
                    content_mode: "ASPECT_FIT",
                    url: undefined,
                },
            },
            product_3: {
                product_id: undefined,
                product_image: {
                    content_mode: "ASPECT_FIT",
                    url: undefined,
                },
            },
            product_4: {
                product_id: undefined,
                product_image: {
                    content_mode: "ASPECT_FIT",
                    url: undefined,
                },
            },
            product_5: {
                product_id: undefined,
                product_image: {
                    content_mode: "ASPECT_FIT",
                    url: undefined,
                },
            },
            product_6: {
                product_id: undefined,
                product_image: {
                    content_mode: "ASPECT_FIT",
                    url: undefined,
                },
            },
            product_7: {
                product_id: undefined,
                product_image: {
                    content_mode: "ASPECT_FIT",
                    url: undefined,
                },
            },
        },
    }),
    [ComponentTypes.IMAGE]: fromJS({
        ...defaults,
        component: ComponentTypes.IMAGE,
        image: {
            url: "",
        },
    }),
    [ComponentTypes.LABEL]: fromJS({
        ...defaults,
        component: ComponentTypes.LABEL,
        label: {
            font: "arial",
            font_color: "#000000",
            font_size: 14,
            font_style_bold: false,
            font_style_italic: false,
            font_style_underline: false,
            text: "",
            text_alignment: "Center",
        },
    }),
};

export const permissionLabels = {
    OWNER: "OWNER",
    READ_ONLY: "VIEWER",
    READ_WRITE: "EDITOR",
};

export const BoardDimensions = {
    A3: {
        height: 841,
        name: "A3",
        value: "A3",
        width: 1190,
    },
    A4: {
        height: 595,
        name: "A4",
        value: "A4",
        width: 841,
    },
    A5: {
        height: 419,
        name: "A5",
        value: "A5",
        width: 595,
    },
    CUSTOM: {
        name: "Custom",
        value: "CUSTOM",
    },
    LEGAL: {
        height: 612,
        name: "US Legal",
        value: "LEGAL",
        width: 1008,
    },
    LETTER: {
        height: 612,
        name: "US Letter",
        value: "LETTER",
        width: 792,
    },
};

export const PAGE_LIMIT = 100;

export const NUMBER_OF_LABELS = 3;

export const LabelFieldNames = ["name", "style_number", "price"];

export const ContentMode = Map({
    ASPECT_FILL: "cover",
    ASPECT_FIT: "contain",
    FILL: "fill",
});

export const Fonts = Map({
    arial: "Arial, Helvetica, sans-serif",
    lato: "'Lato', sans-serif",
    times: "'Times New Roman', Times, serif",
});

export const ZoomScale = {
    max: 2,
    min: 0.25,
};
